import React, {FC, ReactNode, useLayoutEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ContactSourcesEnum} from '../../../../models/api/contactModel';
import {ContactCardActionsWrapper} from '../../common/contactCardActionsWrapper';
import {ContactCardCopyButton} from '../../common/contactCardCopyButton';
import {ContactCardExternalLinkButton} from '../../common/contactCardExternalLinkButton';

/*
 * Constants.
 */

const singleLineItemScrollHeight = 27; // px.

/*
 * Props.
 */

interface ContactCardSectionItemProps {
  title?: ReactNode;
  copyInfo: string;
  handle?: string;
  source?: ContactSourcesEnum;
}

/*
 * Style.
 */

const StyledSectionItemWrapperDiv = styled.div`
  display: contents;
`;

const StyledSectionItemTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  line-height: 16px;
  height: 16px;
  color: ${(p) => p.theme.greys.shade70};
  max-width: 124px;

  &:not(:empty) {
    margin-right: 12px;
  }
`;

interface StyledSectionItemContentDivProps {
  $shouldAddNegativeBottomMargin: boolean;
}
const StyledSectionItemContentDiv = styled.div<StyledSectionItemContentDivProps>`
  position: relative;
  overflow: hidden;
  line-height: 16px;
  padding-top: 5px;
  margin-top: -5px;

  // We need negative margin so the button is not cut off from the overflow hidden.
  ${(p) =>
    p.$shouldAddNegativeBottomMargin &&
    css`
      margin-bottom: -14px;
    `}
`;

const StyledItemContentChildWrapperDiv = styled.div`
  line-height: 16px;
`;

/*
 * Component.
 */

export const ContactCardSectionItem: FC<ContactCardSectionItemProps> = (props) => {
  const {title, children, copyInfo, handle, source} = props;
  const [shouldAddNegativeBottomMargin, setShouldAddNegativeBottomMargin] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!contentRef.current) {
      return;
    }
    setShouldAddNegativeBottomMargin(contentRef.current.scrollHeight <= singleLineItemScrollHeight);
  }, []);

  return (
    <StyledSectionItemWrapperDiv className={buildHoverParentClassName()}>
      <StyledSectionItemTitleDiv>
        <TooltipCoordinator render={() => title}>
          <TooltipOverflowCondition>{title}</TooltipOverflowCondition>
        </TooltipCoordinator>
      </StyledSectionItemTitleDiv>
      <StyledSectionItemContentDiv
        ref={contentRef}
        $shouldAddNegativeBottomMargin={shouldAddNegativeBottomMargin}
      >
        <StyledItemContentChildWrapperDiv>{children}</StyledItemContentChildWrapperDiv>
        <ContactCardActionsWrapper>
          {maybeRenderExternalLinkButton(handle, source)}
          <ContactCardCopyButton copyValue={copyInfo} />
        </ContactCardActionsWrapper>
      </StyledSectionItemContentDiv>
    </StyledSectionItemWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderExternalLinkButton(handle?: string, source?: ContactSourcesEnum) {
  if (!handle || !source) {
    return null;
  }
  return <ContactCardExternalLinkButton handle={handle} source={source} />;
}
